import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import {HttpResponse} from '@angular/common/http';
import 'rxjs/add/observable/forkJoin';
import 'rxjs/add/operator/map';
import {Report} from '../../models/report.model';
import {DEAL_PAYROLL_CALCULATION_REVISION, isPlanfixDealPayroll} from '../../models/deal-payroll.model';
import {formatReportFields} from '../../modals/report-datepicker-report.mapper';
import {selectMixedGroupReportRows} from '../../services/deal-payroll-group-report.adapter';
import {dealPayrollMonthFromRange} from '../../services/report.service';
import { UpdateService } from '../../modals/report.service';
import { ReportService } from '../../services/report.service';
import {
  normalizeWorkingReports,
  sortWorkingReports,
  workingReportCurrency
} from './my-working-report-collection';
import { MyWorkingReportPayrollPresentation } from './my-working-report-payroll.presentation';
import { LoadedWorkingReports, ReportViewModel, RowVisibility } from './my-working-report.types';
import {ReportSessionState} from './my-working-report.types';

const REPORT_RELOAD_INVALID = 'Saved report cannot be reloaded';

@Injectable()
export class MyWorkingReportState {
  constructor(
    public reportService: ReportService,
    private updateService: UpdateService
  ) {
  }

  public updates(): Observable<boolean> {
    return this.updateService.update$;
  }

  public clearUpdate(): void {
    this.updateService.clearNotification();
  }

  public reloadReports(state: ReportSessionState, userIds: string[]): Observable<ReportViewModel[]> {
    if (!state.reportDateFrom || !state.reportDateTo) { throw new Error(REPORT_RELOAD_INVALID); }
    const request = state.isGroupMode && state.groupId
      ? this.reportService.getReportByGroupId({groupId: state.groupId,
        from: state.reportDateFrom, to: state.reportDateTo}).map(response => response.body)
      : this.reloadUsers(state, userIds);
    return request.map(reports => this.prepareReloadedReports(reports, state));
  }

  private reloadUsers(state: ReportSessionState, userIds: string[]): Observable<Report[]> {
    if (!userIds.length) { throw new Error(REPORT_RELOAD_INVALID); }
    return Observable.forkJoin(userIds.map(id => this.reportService.getReportByUserId(
      state.reportDateFrom, state.reportDateTo, id).map((response: HttpResponse<Report[]>) => response.body)))
      .map(rows => rows.reduce((reports: Report[], row: Report[]) => reports.concat(row), []));
  }

  private prepareReloadedReports(reports: Report[], state: ReportSessionState): ReportViewModel[] {
    const month = dealPayrollMonthFromRange(state.reportDateFrom, state.reportDateTo);
    const valid = selectMixedGroupReportRows(reports, month);
    if (!valid || valid.some(report => isPlanfixDealPayroll(report)
      && (!report.dealPayrollBreakdown
        || report.dealPayrollBreakdown.calculationRevision !== DEAL_PAYROLL_CALCULATION_REVISION))) {
      throw new Error(REPORT_RELOAD_INVALID);
    }
    valid.forEach(report => formatReportFields(report));
    return valid as Object as ReportViewModel[];
  }

  public loadReports(
    reports: ReportViewModel[],
    payrollPresentation: MyWorkingReportPayrollPresentation
  ): LoadedWorkingReports {
    const normalized = normalizeWorkingReports(
      reports, <RowVisibility[]>this.reportService.rowNames, payrollPresentation);
    this.reportService.reportsMin = normalized;
    this.reportService.allReports = this.reportService.reportsMin.concat(this.reportService.filteredReports);
    sortWorkingReports(normalized);
    return {
      reports: normalized,
      rowNames: this.reportService.rowNames,
      currency: workingReportCurrency(normalized)
    };
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/my-working-report/my-working-report-state.ts